import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Container,
    Typography,
    Card,
    CardContent,
    Paper,
    Button,
    Chip,
    CircularProgress,
    Alert,
    Avatar,
    Divider,
    IconButton,
    Tooltip,
    FormControl,
    Select,
    MenuItem,
    TextField,
    Autocomplete,
    Checkbox,
    FormControlLabel
} from '@mui/material';
import {
    ArrowBack,
    Edit,
    Email,
    Phone,
    Business,
    Person,
    AccountTree,
    WorkOutline,
    SupervisorAccount,
    Groups,
    Save,
    Close,
    Add,
    Remove
} from '@mui/icons-material';
import { Layout } from '@layout/Layout';
import useEmployeesStore from '@store/EmployeesStore';
import { useAppTheme } from '@contexts/ThemeContext';
import { getEmployeeById } from '@/services/HrService/EmployeesService';

interface Manager {
    id: string;
    name: string;
    email: string;
    role_name?: string;
    dept_name?: string;
    level: number;
}

interface DirectReport {
    id: string;
    name: string;
    email: string;
    role_name?: string;
    dept_name?: string;
}

interface EmployeeData {
    id: string;
    name: string;
    email: string;
    contact?: string;
    role_name?: string;
    role_detail?: string;
    dept_name?: string;
    status?: string;
    gender?: string;
    date_of_birth?: string;
    hire_date?: string;
    experience_years?: number;
    department?: string;
    created_at?: string;
}

interface ProfileData {
    employee: EmployeeData & { dept_id?: number; reporting_manager?: number[] | string };
    managerChain: Manager[];
    directReports: DirectReport[];
}

interface Department {
    id: number;
    dept_name: string;
}

interface EmployeeOption {
    id: string;
    name: string;
    email: string;
    role_name?: string;
    dept_name?: string;
}

interface EditData {
    dept_id: number | null;
    reporting_manager: string[];
    skip_punchin_reminder: boolean;
}

const EmployeeProfilePage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { isDarkMode } = useAppTheme();

    const {
        employeeProfile,
        profileLoading,
        error,
        fetchEmployeeProfile,
        clearEmployeeProfile,
        resetErrors,
        departments,
        fetchDepartments,
        employees,
        fetchEmployees,
        updateEmployeeData
    } = useEmployeesStore();

    // Edit mode state
    const [isEditingStructure, setIsEditingStructure] = useState(false);
    const [editData, setEditData] = useState<EditData>({
        dept_id: null,
        reporting_manager: [],
        skip_punchin_reminder: false
    });
    const [allEmployees, setAllEmployees] = useState<EmployeeOption[]>([]);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);

    useEffect(() => {
        if (id) {
            fetchEmployeeProfile(id);
        }
        // Fetch departments and employees for dropdowns
        fetchDepartments();
        fetchEmployees(1, 100); // Fetch employees for autocomplete (max 100)

        return () => {
            clearEmployeeProfile();
            resetErrors();
        };
    }, [id]);

    // Update allEmployees when employees data changes
    useEffect(() => {
        if (employees) {
            setAllEmployees(employees.map((emp: any) => ({
                id: String(emp.id),
                name: emp.name,
                email: emp.email,
                role_name: emp.role_name,
                dept_name: emp.dept_name
            })));
        }
    }, [employees]);

    // Initialize edit data when profile loads or edit mode starts
    useEffect(() => {
        if (employeeProfile && isEditingStructure) {
            const profile = employeeProfile as ProfileData;
            const employee = profile.employee;

            // Parse reporting_manager - could be JSON string or array
            let managerIds: string[] = [];
            if (employee.reporting_manager) {
                if (typeof employee.reporting_manager === 'string') {
                    try {
                        const parsed = JSON.parse(employee.reporting_manager);
                        managerIds = Array.isArray(parsed) ? parsed.map(String) : [String(parsed)];
                    } catch {
                        managerIds = [String(employee.reporting_manager)];
                    }
                } else if (Array.isArray(employee.reporting_manager)) {
                    managerIds = employee.reporting_manager.map(String);
                }
            }

            setEditData({
                dept_id: employee.dept_id || null,
                reporting_manager: managerIds,
                skip_punchin_reminder: !!(employee as any).skip_punchin_reminder
            });
        }
    }, [employeeProfile, isEditingStructure]);

    const profileData = employeeProfile as ProfileData | null;

    const formatDate = (dateString?: string) => {
        if (!dateString) return '-';
        try {
            return new Date(dateString).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            });
        } catch {
            return '-';
        }
    };

    const getStatusColor = (status?: string) => {
        return status === 'active' ? '#4caf50' : '#f44336';
    };

    const getInitials = (name?: string) => {
        if (!name) return '?';
        return name
            .split(' ')
            .map(part => part.charAt(0))
            .join('')
            .toUpperCase()
            .slice(0, 2);
    };

    // Get selected managers for Autocomplete
    const getSelectedManagers = (): EmployeeOption[] => {
        return allEmployees.filter(emp => editData.reporting_manager.includes(emp.id));
    };

    // Handle starting edit mode
    const handleStartEdit = () => {
        setIsEditingStructure(true);
        setSaveError(null);
    };

    // Handle cancel edit
    const handleCancelEdit = () => {
        setIsEditingStructure(false);
        setSaveError(null);
    };

    // Handle save changes
    const handleSaveChanges = async () => {
        if (!id) return;

        setSaving(true);
        setSaveError(null);

        try {
            // Update current employee's department, reporting manager, and reminder settings
            await updateEmployeeData(id, {
                dept_id: editData.dept_id,
                reporting_manager: JSON.stringify(editData.reporting_manager),
                skip_punchin_reminder: editData.skip_punchin_reminder
            });

            // Refresh the profile data
            await fetchEmployeeProfile(id);
            setIsEditingStructure(false);
        } catch (err: any) {
            setSaveError(err.message || 'Failed to save changes');
        } finally {
            setSaving(false);
        }
    };

    // Handle adding a direct report
    const handleAddDirectReport = async (newReport: EmployeeOption) => {
        if (!id) return;

        setSaving(true);
        setSaveError(null);

        try {
            // Get the employee being added as direct report
            const employeeToUpdate = allEmployees.find(emp => emp.id === newReport.id);
            if (!employeeToUpdate) return;

            // Fetch that employee's current reporting_manager
            // We need to add the current employee as their manager
            const response = await getEmployeeById(newReport.id);
            const data = response;

            let currentManagers: string[] = [];
            if (data.data?.reporting_manager) {
                if (typeof data.data.reporting_manager === 'string') {
                    try {
                        const parsed = JSON.parse(data.data.reporting_manager);
                        currentManagers = Array.isArray(parsed) ? parsed.map(String) : [String(parsed)];
                    } catch {
                        currentManagers = [String(data.data.reporting_manager)];
                    }
                } else if (Array.isArray(data.data.reporting_manager)) {
                    currentManagers = data.data.reporting_manager.map(String);
                }
            }

            // Add current employee as manager if not already
            const currentEmployeeId = String(id);
            if (!currentManagers.includes(currentEmployeeId)) {
                currentManagers.push(currentEmployeeId);
            }

            // Update the direct report's reporting_manager
            await updateEmployeeData(newReport.id, {
                reporting_manager: JSON.stringify(currentManagers)
            });

            // Refresh profile
            await fetchEmployeeProfile(id);
        } catch (err: any) {
            setSaveError(err.message || 'Failed to add direct report');
        } finally {
            setSaving(false);
        }
    };

    // Handle removing a direct report
    const handleRemoveDirectReport = async (reportId: string) => {
        if (!id) return;

        setSaving(true);
        setSaveError(null);

        try {
            // Fetch that employee's current reporting_manager
            const response = await getEmployeeById(reportId);
            const data = response;

            let currentManagers: string[] = [];
            if (data.data?.reporting_manager) {
                if (typeof data.data.reporting_manager === 'string') {
                    try {
                        const parsed = JSON.parse(data.data.reporting_manager);
                        currentManagers = Array.isArray(parsed) ? parsed.map(String) : [String(parsed)];
                    } catch {
                        currentManagers = [String(data.data.reporting_manager)];
                    }
                } else if (Array.isArray(data.data.reporting_manager)) {
                    currentManagers = data.data.reporting_manager.map(String);
                }
            }

            // Remove current employee from their managers
            const currentEmployeeId = String(id);
            currentManagers = currentManagers.filter(m => m !== currentEmployeeId);

            // Update the direct report's reporting_manager
            await updateEmployeeData(reportId, {
                reporting_manager: JSON.stringify(currentManagers)
            });

            // Refresh profile
            await fetchEmployeeProfile(id);
        } catch (err: any) {
            setSaveError(err.message || 'Failed to remove direct report');
        } finally {
            setSaving(false);
        }
    };

    if (profileLoading) {
        return (
            <Layout>
                <Container maxWidth="lg">
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
                        <CircularProgress size={60} />
                    </Box>
                </Container>
            </Layout>
        );
    }

    if (error) {
        return (
            <Layout>
                <Container maxWidth="lg">
                    <Box sx={{ p: 3 }}>
                        <Button
                            startIcon={<ArrowBack />}
                            onClick={() => navigate('/employees')}
                            sx={{ mb: 2 }}
                        >
                            Back to Employees
                        </Button>
                        <Alert severity="error">{error}</Alert>
                    </Box>
                </Container>
            </Layout>
        );
    }

    if (!profileData) {
        return (
            <Layout>
                <Container maxWidth="lg">
                    <Box sx={{ p: 3 }}>
                        <Button
                            startIcon={<ArrowBack />}
                            onClick={() => navigate('/employees')}
                            sx={{ mb: 2 }}
                        >
                            Back to Employees
                        </Button>
                        <Alert severity="info">Employee profile not found</Alert>
                    </Box>
                </Container>
            </Layout>
        );
    }

    const { employee, managerChain, directReports } = profileData;

    return (
        <Layout>
            <Container maxWidth="lg">
                <Box sx={{ p: 3 }}>
                    {/* Header with Back Button */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                        <Button
                            startIcon={<ArrowBack />}
                            onClick={() => navigate('/employees')}
                            variant="outlined"
                        >
                            Back to Employees
                        </Button>
                        <Button
                            startIcon={<Edit />}
                            variant="contained"
                            onClick={() => navigate(`/employees/${employee.id}/edit`)}
                        >
                            Edit Profile
                        </Button>
                    </Box>

                    {/* Profile Header Card */}
                    <Card sx={{ mb: 3 }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                                <Avatar
                                    sx={{
                                        width: 100,
                                        height: 100,
                                        fontSize: '2rem',
                                        bgcolor: isDarkMode ? '#1976d2' : '#1565c0'
                                    }}
                                >
                                    {getInitials(employee.name)}
                                </Avatar>
                                <Box sx={{ flex: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                                        <Typography variant="h4" component="h1">
                                            {employee.name}
                                        </Typography>
                                        <Chip
                                            label={employee.status || 'active'}
                                            size="small"
                                            sx={{
                                                backgroundColor: getStatusColor(employee.status) + '20',
                                                color: getStatusColor(employee.status),
                                                fontWeight: 600,
                                                textTransform: 'capitalize'
                                            }}
                                        />
                                    </Box>
                                    <Typography variant="subtitle1" color="textSecondary" sx={{ mb: 1 }}>
                                        {employee.role_name || 'No Role Assigned'}
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <Email fontSize="small" color="action" />
                                            <Typography variant="body2">{employee.email}</Typography>
                                        </Box>
                                        {employee.contact && (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <Phone fontSize="small" color="action" />
                                                <Typography variant="body2">{employee.contact}</Typography>
                                            </Box>
                                        )}
                                        {employee.dept_name && (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <Business fontSize="small" color="action" />
                                                <Typography variant="body2">{employee.dept_name}</Typography>
                                            </Box>
                                        )}
                                    </Box>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>

                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
                        {/* Personal Information */}
                        <Card>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                    <Person color="primary" />
                                    <Typography variant="h6">Personal Information</Typography>
                                </Box>
                                <Divider sx={{ mb: 2 }} />
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="body2" color="textSecondary">Gender</Typography>
                                        <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                                            {employee.gender || '-'}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="body2" color="textSecondary">Date of Birth</Typography>
                                        <Typography variant="body2">{formatDate(employee.date_of_birth)}</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="body2" color="textSecondary">Email</Typography>
                                        <Typography variant="body2">{employee.email}</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="body2" color="textSecondary">Contact</Typography>
                                        <Typography variant="body2">{employee.contact || '-'}</Typography>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>

                        {/* Employment Details */}
                        <Card>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                    <WorkOutline color="primary" />
                                    <Typography variant="h6">Employment Details</Typography>
                                </Box>
                                <Divider sx={{ mb: 2 }} />
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="body2" color="textSecondary">Role</Typography>
                                        <Typography variant="body2">{employee.role_name || '-'}</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography variant="body2" color="textSecondary">Department</Typography>
                                        {isEditingStructure ? (
                                            <FormControl size="small" sx={{ minWidth: 150 }}>
                                                <Select
                                                    value={editData.dept_id || ''}
                                                    onChange={(e) => setEditData({ ...editData, dept_id: e.target.value as number || null })}
                                                    displayEmpty
                                                >
                                                    <MenuItem value="">
                                                        <em>None</em>
                                                    </MenuItem>
                                                    {(departments as Department[]).map((dept) => (
                                                        <MenuItem key={dept.id} value={dept.id}>
                                                            {dept.dept_name}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        ) : (
                                            <Typography variant="body2">{employee.dept_name || '-'}</Typography>
                                        )}
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="body2" color="textSecondary">Hire Date</Typography>
                                        <Typography variant="body2">{formatDate(employee.hire_date)}</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="body2" color="textSecondary">Experience</Typography>
                                        <Typography variant="body2">
                                            {employee.experience_years !== undefined && employee.experience_years !== null
                                                ? `${employee.experience_years} years`
                                                : '-'}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="body2" color="textSecondary">Status</Typography>
                                        <Chip
                                            label={employee.status || 'active'}
                                            size="small"
                                            sx={{
                                                backgroundColor: getStatusColor(employee.status) + '20',
                                                color: getStatusColor(employee.status),
                                                fontWeight: 500,
                                                textTransform: 'capitalize'
                                            }}
                                        />
                                    </Box>
                                    {isEditingStructure && (
                                        <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={editData.skip_punchin_reminder}
                                                        onChange={(e) => setEditData({
                                                            ...editData,
                                                            skip_punchin_reminder: e.target.checked
                                                        })}
                                                    />
                                                }
                                                label={
                                                    <Box>
                                                        <Typography variant="body2">Skip punch-in/punch-out reminders</Typography>
                                                        <Typography variant="caption" color="textSecondary">
                                                            This employee won't receive attendance reminder emails
                                                        </Typography>
                                                    </Box>
                                                }
                                            />
                                        </Box>
                                    )}
                                </Box>
                            </CardContent>
                        </Card>
                    </Box>

                    {/* Reporting Structure */}
                    <Card sx={{ mt: 3 }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <AccountTree color="primary" />
                                    <Typography variant="h6">Reporting Structure</Typography>
                                </Box>
                                {!isEditingStructure ? (
                                    <Button
                                        startIcon={<Edit />}
                                        variant="outlined"
                                        size="small"
                                        onClick={handleStartEdit}
                                    >
                                        Edit Structure
                                    </Button>
                                ) : (
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <Button
                                            startIcon={<Close />}
                                            variant="outlined"
                                            size="small"
                                            onClick={handleCancelEdit}
                                            disabled={saving}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            startIcon={saving ? <CircularProgress size={16} /> : <Save />}
                                            variant="contained"
                                            size="small"
                                            onClick={handleSaveChanges}
                                            disabled={saving}
                                        >
                                            {saving ? 'Saving...' : 'Save Changes'}
                                        </Button>
                                    </Box>
                                )}
                            </Box>

                            {saveError && (
                                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSaveError(null)}>
                                    {saveError}
                                </Alert>
                            )}

                            <Divider sx={{ mb: 3 }} />

                            {/* Manager Chain */}
                            <Box sx={{ mb: 4 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                    <SupervisorAccount fontSize="small" color="action" />
                                    <Typography variant="subtitle2" color="textSecondary">
                                        Reports To {!isEditingStructure && managerChain && managerChain.length > 0 && `(${managerChain.length} Manager${managerChain.length > 1 ? 's' : ''})`}
                                    </Typography>
                                </Box>

                                {isEditingStructure ? (
                                    <Box sx={{ pl: 2 }}>
                                        <Autocomplete
                                            multiple
                                            options={allEmployees.filter(emp => emp.id !== id)}
                                            value={getSelectedManagers()}
                                            onChange={(_, newValue) => setEditData({
                                                ...editData,
                                                reporting_manager: newValue.map(emp => emp.id)
                                            })}
                                            getOptionLabel={(option) => option.name}
                                            renderOption={(props, option) => (
                                                <li {...props} key={option.id}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <Avatar sx={{ width: 28, height: 28, fontSize: '0.75rem', bgcolor: '#1976d2' }}>
                                                            {getInitials(option.name)}
                                                        </Avatar>
                                                        <Box>
                                                            <Typography variant="body2">{option.name}</Typography>
                                                            <Typography variant="caption" color="textSecondary">
                                                                {option.role_name || 'No Role'} {option.dept_name ? `- ${option.dept_name}` : ''}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </li>
                                            )}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    placeholder="Select reporting managers..."
                                                    size="small"
                                                />
                                            )}
                                            isOptionEqualToValue={(option, value) => option.id === value.id}
                                        />
                                    </Box>
                                ) : managerChain && managerChain.length > 0 ? (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pl: 2 }}>
                                        {managerChain.map((manager) => (
                                            <Paper
                                                key={manager.id}
                                                elevation={0}
                                                sx={{
                                                    p: 2,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 2,
                                                    backgroundColor: isDarkMode ? 'rgba(25, 118, 210, 0.08)' : 'rgba(25, 118, 210, 0.04)',
                                                    borderLeft: '3px solid #1976d2'
                                                }}
                                            >
                                                <Avatar
                                                    sx={{
                                                        width: 40,
                                                        height: 40,
                                                        bgcolor: '#1976d2',
                                                        fontSize: '0.875rem'
                                                    }}
                                                >
                                                    {getInitials(manager.name)}
                                                </Avatar>
                                                <Box sx={{ flex: 1 }}>
                                                    <Typography variant="body2" fontWeight={500}>
                                                        {manager.name}
                                                        <Chip
                                                            label="Reporting Manager"
                                                            size="small"
                                                            sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                                                        />
                                                    </Typography>
                                                    <Typography variant="caption" color="textSecondary">
                                                        {manager.role_name || 'No Role'} {manager.dept_name ? `- ${manager.dept_name}` : ''}
                                                    </Typography>
                                                </Box>
                                                <Tooltip title="View Profile">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => navigate(`/employees/${manager.id}/profile`)}
                                                    >
                                                        <Person fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Paper>
                                        ))}
                                    </Box>
                                ) : (
                                    <Typography variant="body2" color="textSecondary" sx={{ pl: 2 }}>
                                        No reporting manager assigned
                                    </Typography>
                                )}
                            </Box>

                            {/* Direct Reports */}
                            <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                    <Groups fontSize="small" color="action" />
                                    <Typography variant="subtitle2" color="textSecondary">
                                        Direct Reports ({directReports?.length || 0})
                                    </Typography>
                                </Box>

                                {/* Add Direct Report - shown in edit mode */}
                                {isEditingStructure && (
                                    <Box sx={{ pl: 2, mb: 2 }}>
                                        <Autocomplete
                                            options={allEmployees.filter(emp =>
                                                emp.id !== id &&
                                                !directReports?.some(dr => dr.id === emp.id)
                                            )}
                                            getOptionLabel={(option) => option.name}
                                            renderOption={(props, option) => (
                                                <li {...props} key={option.id}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <Avatar sx={{ width: 28, height: 28, fontSize: '0.75rem', bgcolor: '#4caf50' }}>
                                                            {getInitials(option.name)}
                                                        </Avatar>
                                                        <Box>
                                                            <Typography variant="body2">{option.name}</Typography>
                                                            <Typography variant="caption" color="textSecondary">
                                                                {option.role_name || 'No Role'} {option.dept_name ? `- ${option.dept_name}` : ''}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </li>
                                            )}
                                            onChange={(_, newValue) => {
                                                if (newValue) {
                                                    handleAddDirectReport(newValue);
                                                }
                                            }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    placeholder="Add a direct report..."
                                                    size="small"
                                                    InputProps={{
                                                        ...params.InputProps,
                                                        startAdornment: (
                                                            <>
                                                                <Add fontSize="small" sx={{ color: 'action.active', mr: 1 }} />
                                                                {params.InputProps.startAdornment}
                                                            </>
                                                        )
                                                    }}
                                                />
                                            )}
                                            value={null}
                                            disabled={saving}
                                        />
                                    </Box>
                                )}

                                {directReports && directReports.length > 0 ? (
                                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 2, pl: 2 }}>
                                        {directReports.map((report) => (
                                            <Paper
                                                key={report.id}
                                                elevation={0}
                                                sx={{
                                                    p: 2,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 2,
                                                    backgroundColor: isDarkMode ? 'rgba(76, 175, 80, 0.08)' : 'rgba(76, 175, 80, 0.04)',
                                                    borderLeft: '3px solid #4caf50',
                                                    cursor: isEditingStructure ? 'default' : 'pointer',
                                                    '&:hover': {
                                                        backgroundColor: isDarkMode ? 'rgba(76, 175, 80, 0.15)' : 'rgba(76, 175, 80, 0.08)'
                                                    }
                                                }}
                                                onClick={() => !isEditingStructure && navigate(`/employees/${report.id}/profile`)}
                                            >
                                                <Avatar
                                                    sx={{
                                                        width: 36,
                                                        height: 36,
                                                        bgcolor: '#4caf50',
                                                        fontSize: '0.8rem'
                                                    }}
                                                >
                                                    {getInitials(report.name)}
                                                </Avatar>
                                                <Box sx={{ flex: 1, overflow: 'hidden' }}>
                                                    <Typography variant="body2" fontWeight={500} noWrap>
                                                        {report.name}
                                                    </Typography>
                                                    <Typography variant="caption" color="textSecondary" noWrap>
                                                        {report.role_name || 'No Role'}
                                                    </Typography>
                                                </Box>
                                                {isEditingStructure && (
                                                    <Tooltip title="Remove as direct report">
                                                        <IconButton
                                                            size="small"
                                                            color="error"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleRemoveDirectReport(report.id);
                                                            }}
                                                            disabled={saving}
                                                        >
                                                            <Remove fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </Paper>
                                        ))}
                                    </Box>
                                ) : (
                                    <Typography variant="body2" color="textSecondary" sx={{ pl: 2 }}>
                                        No direct reports
                                    </Typography>
                                )}
                            </Box>
                        </CardContent>
                    </Card>
                </Box>
            </Container>
        </Layout>
    );
};

export default EmployeeProfilePage;
